import React, { useMemo } from 'react';
import { Bucket, BucketTheme } from '../types';
import { Trophy, Calendar, Edit2, Trash2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../i18n';
import { ExternalTodo } from '../App';

interface BucketRowProps {
  bucket: Bucket;
  externalTodos?: ExternalTodo[];
  onEdit: (bucket: Bucket) => void;
  onDelete: (id: string) => void;
}

const THEME_COLORS: Record<BucketTheme, { bg: string; text: string; bar: string }> = {
  [BucketTheme.Blue]: { bg: 'bg-blue-500/20', text: 'text-blue-400', bar: 'bg-blue-500' },
  [BucketTheme.Purple]: { bg: 'bg-purple-500/20', text: 'text-purple-400', bar: 'bg-purple-500' },
  [BucketTheme.Green]: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', bar: 'bg-emerald-500' },
  [BucketTheme.Orange]: { bg: 'bg-orange-500/20', text: 'text-orange-400', bar: 'bg-orange-500' },
  [BucketTheme.Pink]: { bg: 'bg-pink-500/20', text: 'text-pink-400', bar: 'bg-pink-500' },
  [BucketTheme.Red]: { bg: 'bg-red-500/20', text: 'text-red-400', bar: 'bg-red-500' },
  [BucketTheme.Teal]: { bg: 'bg-teal-500/20', text: 'text-teal-400', bar: 'bg-teal-500' },
  [BucketTheme.Yellow]: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', bar: 'bg-yellow-500' },
  [BucketTheme.Indigo]: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', bar: 'bg-indigo-500' },
  [BucketTheme.Cyan]: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', bar: 'bg-cyan-500' },
  [BucketTheme.Lime]: { bg: 'bg-lime-500/20', text: 'text-lime-400', bar: 'bg-lime-500' },
  [BucketTheme.Rose]: { bg: 'bg-rose-500/20', text: 'text-rose-400', bar: 'bg-rose-500' },
};

export const BucketRow: React.FC<BucketRowProps> = ({ bucket, externalTodos = [], onEdit, onDelete }) => {
  const { t } = useLanguage();
  const colors = THEME_COLORS[bucket.theme];

  const stats = useMemo(() => {
    // Combine for stats
    const allTodos = [...bucket.todos, ...externalTodos.map(et => et.todo)];

    const totalPointsInList = allTodos.reduce((sum, t) => sum + (t.points || 1), 0);
    const completedPoints = allTodos
      .reduce((sum, t) => sum + (t.completed ? (t.points || 1) : 0) + (t.accumulatedPoints || 0), 0);
      
    let percent = 0;
    let target = 0;
    let isGoalReached = false;

    if (bucket.goal) {
        target = bucket.goal.targetPoints;
        percent = Math.min(100, Math.round((completedPoints / target) * 100));
        isGoalReached = completedPoints >= target;
    } else {
        target = totalPointsInList;
        percent = target === 0 ? 0 : Math.round((completedPoints / target) * 100);
    }
      
    return { percent, completedPoints, target, isGoalReached, totalCount: allTodos.length };
  }, [bucket.todos, bucket.goal, externalTodos]);

  return (
    <div 
        id={`bucket-${bucket.id}`}
        className={`group w-full p-4 rounded-xl border transition-all flex items-center gap-4 ${stats.isGoalReached ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-slate-900/50 border-white/5 hover:border-white/10 hover:bg-slate-800/50'}`}
    >
      {/* Icon / Theme */}
      <div className={`w-12 h-12 rounded-full ${stats.isGoalReached ? 'bg-yellow-500/20 text-yellow-400' : colors.bg + ' ' + colors.text} flex items-center justify-center flex-shrink-0 relative`}>
        <Trophy size={20} className={stats.isGoalReached ? 'animate-pulse' : ''} />
        {stats.isGoalReached && <div className="absolute top-0 right-0 w-3 h-3 bg-yellow-400 rounded-full border-2 border-slate-900"></div>}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
        
        {/* Name & Basic Stats */}
        <div className="min-w-0">
          <h3 className="font-bold text-slate-200 truncate flex items-center gap-2">
            {bucket.name}
            {stats.isGoalReached && <span className="text-[10px] bg-yellow-500/20 text-yellow-300 px-1.5 py-0.5 rounded border border-yellow-500/20">{t.goalReached}</span>}
          </h3>
          <p className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <span>{stats.totalCount} {t.tasks}</span>
             {bucket.goal?.title && (
               <>
                <span>•</span>
                <span className="text-yellow-500/80 font-medium">{bucket.goal.title}</span>
               </>
            )}
            {bucket.goal?.deadline && (
               <>
                <span>•</span>
                <span className="flex items-center gap-1">
                    <Calendar size={10} />
                    {new Date(bucket.goal.deadline).toLocaleDateString()}
                </span>
               </>
            )}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full">
            <div className="flex justify-between text-xs mb-1.5">
                <span className="text-slate-400">{t.bucketProgress}</span>
                <span className={colors.text}>{stats.percent}%</span>
            </div>
            <div className="h-2 w-full bg-slate-950 rounded-full overflow-hidden relative">
                <div 
                    className={`h-full ${stats.isGoalReached ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : colors.bar} transition-all duration-500`}
                    style={{ width: `${stats.percent}%` }}
                />
            </div>
            <div className="text-[10px] text-slate-600 mt-1 text-right">
                {stats.completedPoints} / {stats.target} {t.pts}
            </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pl-4 border-l border-white/5">
        <button 
            onClick={() => onEdit(bucket)}
            className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
            title={t.editBucket}
        >
            <Edit2 size={18} />
        </button>
        <button 
            onClick={() => onDelete(bucket.id)}
            className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
            title={t.deleteBucketConfirm}
        >
            <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};
